import type { InputHTMLAttributes, Ref } from "react";

/**
 * A range input, styled without giving up being one.
 *
 * ## Why the track is a background and not an element
 *
 * A slider drawn from divs has to reimplement pointer capture, keyboard steps,
 * `aria-valuenow` and the whole of what a range input already does correctly.
 * The parts a browser will actually let you style - the track and the thumb -
 * are enough, so what is here is CSS on a real `<input type="range">`.
 *
 * The filled portion is a gradient sized from the value, because no browser
 * offers a pseudo-element for "the part behind the thumb".
 */
export function Slider({
  value,
  min = 0,
  max = 1,
  className = "",
  ref,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  ref?: Ref<HTMLInputElement>;
}) {
  const at = Number(value ?? 0);
  const lo = Number(min);
  const hi = Number(max);
  const filled = hi > lo ? ((at - lo) / (hi - lo)) * 100 : 0;

  return (
    <input
      ref={ref}
      type="range"
      value={value}
      min={min}
      max={max}
      className={`gv-slider ${className}`}
      style={{ "--gv-filled": `${filled}%` } as React.CSSProperties}
      {...rest}
    />
  );
}
